import {
  Badge,
  classNames,
  FlexCenter,
  FlexColumn,
  formatAddress,
  ItemAvatar,
  ItemContent,
  ItemSide,
  ItemTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  TextBody,
  Tooltip,
} from "@opensea/ui-kit"
import { InfoFilled } from "@opensea/ui-kit/icons"
import { uniqBy } from "es-toolkit/array"
import { useMemo, useRef } from "react"
import { useTranslations } from "use-intl"
import { Item } from "../../../components/Item"
import { CreditCard } from "../../../components/icons/CreditCard"
import {
  useConnect,
  useConnectors,
  useGetPreviouslyConnectedAccountsForConnector,
} from "../../../polychain/hooks"
import type { Connector } from "../../../types"
import { itemVariants } from "../constants"
import type { PaymentMethod } from "../types"

type Props = {
  children: React.ReactNode
  paymentMethod: PaymentMethod | undefined
  setPaymentMethod?: (paymentMethod: PaymentMethod | undefined) => void
}

const PaymentMethodOptions = ({
  paymentMethod,
  setPaymentMethod,
  children,
}: Props) => {
  return (
    <FlexColumn className="gap-3">
      <RadioGroup
        className="max-h-[330px] overflow-y-auto"
        onValueChange={value => setPaymentMethod?.(value as PaymentMethod)}
        value={paymentMethod}
      >
        {children}
      </RadioGroup>
    </FlexColumn>
  )
}

type CardProps = {
  selected: boolean
  disabledReason?: string
}

const Card = ({ selected, disabledReason }: CardProps) => {
  const t = useTranslations("wallet.PaymentMethodOptions")

  return (
    <Tooltip content={disabledReason}>
      <div>
        <Item
          asChild
          className={classNames(
            itemVariants({ interactive: Boolean(disabledReason) }),
            selected && "!bg-bg-additional-2",
          )}
          disabled={Boolean(disabledReason)}
          interactive
        >
          <Label htmlFor="payment-method-card">
            <CreditCard size={24} />
            <ItemContent>
              <ItemTitle>{t("debitCreditCard")}</ItemTitle>
            </ItemContent>
            <ItemSide>
              <RadioGroupItem id="payment-method-card" value="card" />
            </ItemSide>
          </Label>
        </Item>
      </div>
    </Tooltip>
  )
}

type WalletProps = {
  connectorId: string
  selectedPaymentMethod: PaymentMethod | undefined
}

const Wallet = ({ connectorId }: WalletProps) => {
  const t = useTranslations("wallet.PaymentMethodOptions")

  const { getConnectorStrict } = useConnect()
  const connector = getConnectorStrict(connectorId)

  const getPreviouslyConnectedAccounts =
    useGetPreviouslyConnectedAccountsForConnector()
  const previouslyConnectedAccounts =
    getPreviouslyConnectedAccounts(connectorId)

  const hasPreviouslyConnected = previouslyConnectedAccounts.length > 0
  const paymentMethod = `wallet-${connector.id}`

  return (
    <Item
      asChild
      className={classNames(itemVariants())}
      data-testid={`wallet-${connector.id}`}
      interactive
      key={connector.id}
    >
      <Label htmlFor={paymentMethod}>
        {connector.icon ? (
          <ItemAvatar aria-hidden src={connector.icon} />
        ) : null}
        <ItemContent>
          <ItemTitle>
            {connector.name}
            {hasPreviouslyConnected ? (
              <>
                {" "}
                <TextBody color="text-secondary" weight="regular">
                  {formatAddress(previouslyConnectedAccounts[0].address)}
                </TextBody>
              </>
            ) : null}
          </ItemTitle>
        </ItemContent>
        <ItemSide className="overflow-visible">
          <FlexCenter className="gap-2">
            {connector.isConnected() ? (
              <Badge icon={InfoFilled}>{t("connected")}</Badge>
            ) : connector.isInstalled ? (
              <Badge icon={InfoFilled}>{t("installed")}</Badge>
            ) : hasPreviouslyConnected ? (
              <Badge icon={InfoFilled}>{t("usedPreviously")}</Badge>
            ) : null}
            <RadioGroupItem id={paymentMethod} value={paymentMethod} />
          </FlexCenter>
        </ItemSide>
      </Label>
    </Item>
  )
}

const WalletOptions = ({
  selectedPaymentMethod,
}: {
  selectedPaymentMethod: PaymentMethod | undefined
}) => {
  const { connectors } = useOrderedConnectors()

  const uniqueConnectors = uniqBy(connectors, c => c.name)

  return (
    <>
      {uniqueConnectors.map(connector => {
        return (
          <Wallet
            connectorId={connector.id}
            key={connector.id}
            selectedPaymentMethod={selectedPaymentMethod}
          />
        )
      })}
    </>
  )
}

function useOrderedConnectors() {
  const { connectors, activeConnector, ...rest } = useConnectors()

  // Put active connector first and keep it first even if active connector changes
  const initialActiveConnectorId = useRef(activeConnector?.id)
  const orderedConnectors = useMemo(() => {
    if (!initialActiveConnectorId.current) {
      return connectors
    }
    const result: Connector[] = []
    const initialActiveConnector = connectors.find(
      c => c.id === initialActiveConnectorId.current,
    )
    if (initialActiveConnector) {
      result.push(initialActiveConnector)
    }
    result.push(...connectors.filter(c => c.id !== initialActiveConnector?.id))
    return result
  }, [connectors])

  return {
    connectors: orderedConnectors,
    activeConnector,
    ...rest,
  }
}

export {
  PaymentMethodOptions,
  Card as PaymentMethodOptionsCard,
  Wallet as PaymentMethodOptionsWallet,
  WalletOptions as PaymentMethodOptionsWalletOptions,
}
